var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useContext, useCallback, memo } from "react";
import { onChangeLanguage } from '@localisation/localizationUtils';
import { UXPContext } from "@core/context/Context";
import classNames from 'classnames';
import Select from '@widget/forms/inputs/Select';
import ProfileImage from "@widget/other/ProfileImage";
import { Dropdown } from '@widget/popups/Dropdown';
import { useLoadingFeedback } from '@widget/loaders';
import { useLogout } from '@core/services/useLogout';
import DropdownIndicator from '@widget/other/DropdownIndicator';
import { SafeLink } from '@core/route-context/RouteContext';
/**
 * @export
 * User Profile component
 *
 * This component can be used when building UI without the default header
 *
 * @example Basic usage
 * ```tsx
 * <UserProfile>
 *   <your content>
 * </UserProfile>
 * ```
 *
 * @example Hide default details and logout button
 * ```tsx
 * <UserProfile
 *   hideDetails={true}
 *   hideLogout={true}
 * >
 *   <your content>
 * </UserProfile>
 * ```
 */
const UserProfile = memo((props) => {
    const { hideDetails = false, hideLogout = false, className, children } = props;
    const context = useContext(UXPContext);
    const loading = useLoadingFeedback();
    const [lang, setLang] = useState(context.language);
    const [showProfile, setShowProfile] = useState(false);
    const buttonRef = useRef(null);
    // Get user details directly from context to ensure it's always up to date
    const profileDetails = context.userDetails;
    const handleToggle = useCallback(() => {
        setShowProfile(prev => !prev);
    }, []);
    const logout = useLogout();
    const onLogout = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setShowProfile(false);
        yield logout();
    }), []);
    const handleLanguageChange = useCallback((val) => __awaiter(void 0, void 0, void 0, function* () {
        setLang(val);
        loading.show({ message: context.$L('uxp-core.text.changing-language') });
        yield onChangeLanguage(context, val);
        // Page reloads on success, no need to hide loading
    }), [context]);
    const renderProfileDetails = useCallback(() => {
        if (!profileDetails)
            return null;
        const languages = context.enabledLanguages || [];
        const currentLang = languages.find(l => l.Code === (lang || 'en'));
        return (_jsx("table", { className: 'uxpcore_userprofile__details', children: _jsxs("tbody", { children: [_jsxs("tr", { className: 'uxpcore_userprofile__details-row', children: [_jsx("td", { className: 'uxpcore_userprofile__details-cell uxpcore_userprofile__details-cell--label', children: context.$L('uxp-core.text.name') }), _jsx("td", { className: 'uxpcore_userprofile__details-cell uxpcore_userprofile__details-cell--value', children: profileDetails.FullName })] }), _jsxs("tr", { className: 'uxpcore_userprofile__details-row', children: [_jsx("td", { className: 'uxpcore_userprofile__details-cell uxpcore_userprofile__details-cell--label', children: context.$L('uxp-core.text.email') }), _jsx("td", { className: 'uxpcore_userprofile__details-cell uxpcore_userprofile__details-cell--value', children: profileDetails.Email })] }), languages.length > 1 && (_jsxs("tr", { className: 'uxpcore_userprofile__details-row', children: [_jsx("td", { className: 'uxpcore_userprofile__details-cell uxpcore_userprofile__details-cell--label', children: context.$L('uxp-core.text.language') }), _jsx("td", { className: 'uxpcore_userprofile__details-cell uxpcore_userprofile__details-cell--value', children: _jsx(Select, { selected: lang || 'en', onChange: handleLanguageChange, options: languages, labelField: 'Name', valueField: 'Code', className: 'uxpcore_language_dropdown', renderPlaceholder: {
                                        renderCustomPill: () => (_jsxs("span", { className: "uxpcore_userprofile__lang-pill", children: [(currentLang === null || currentLang === void 0 ? void 0 : currentLang.Name) || lang, _jsx(DropdownIndicator, { isOpen: false })] }))
                                    } }) })] }))] }) }));
    }, [context, lang, profileDetails, handleLanguageChange]);
    if (!profileDetails)
        return null;
    const profileImageUrl = profileDetails.ProfileImageName
        ? `/AccountResources/profilepics/${profileDetails.ProfileImageName}`
        : '';
    const username = profileDetails.FullName;
    const renderDropdownContent = () => (_jsxs("div", { className: "uxpcore_userprofile__panel", children: [_jsx("div", { className: "uxpcore_userprofile__header", children: _jsxs("div", { className: "uxpcore_userprofile__profile-image", children: [_jsx(ProfileImage, { image: profileImageUrl, name: username }), _jsx("div", { className: "uxpcore_userprofile__username", children: username })] }) }), !hideDetails && renderProfileDetails(), children, _jsxs("div", { className: "uxpcore_userprofile__footer", children: [_jsx(SafeLink, { to: "/view/user/profile", className: "uxpcore_userprofile__footer-btn uxpcore_userprofile__footer-btn--profile", children: context.$L('uxp-core.text.my-profile') }), !hideLogout && (_jsx("button", { className: 'uxpcore_userprofile__footer-btn uxpcore_userprofile__footer-btn--logout', onClick: onLogout, children: context.$L('uxp-core.text.logout') }))] })] }));
    return (_jsxs("div", { className: classNames('uxpcore_userprofile__container', "lxp-user-profile-container", className), children: [_jsx("div", { className: classNames("uxpcore_userprofile__button", "profile-button"), ref: buttonRef, onClick: handleToggle, children: _jsx(ProfileImage, { image: profileImageUrl, name: username }) }), _jsx(Dropdown, { externalTriggerRef: buttonRef, content: renderDropdownContent(), position: "bottom-right", isOpen: showProfile, onToggle: handleToggle, contentClassName: "uxpcore_userprofile__dropdown" })] }));
});
export default UserProfile;
